import React, { useCallback, useMemo, useState } from "react";
import { Badge, Button, Drawer, Empty, Input, Menu, Popover, Select, Space, Table, Typography } from "antd";
import { EyeOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import { useStockData } from "../../../../data/hooks/firestore/useStock";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useOrders } from "../../../../data/hooks/firestore/useOrders";
import { ORDER_STATUS_CANCELED, ORDER_STATUS_CANCELLED } from "../../../../constants/OrderConstant";
import utils from "utils";
import styled from "styled-components";
import { StyledSelect } from "../../../../styles/sharedStyles";
import { PRODUCT_COMPANY_GROUPS } from "../../../../constants/ProductConstant";
import useIsMobile from "../../../../hooks/useIsMobile";
import { cartStockLines } from "../../../../functions/productStock";

const { Text } = Typography;
const { Option } = Select;

const LiveStockTab = ({ tableY }) => {
  const { t } = useTranslation();
  const { stockData } = useStockData();
  const { products } = useProducts();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [companyFilter, setCompanyFilter] = useState(localStorage.getItem("liveStockCompanyFilter") || "All");
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();

  const todayDateId = dayjs().format("DDMMYYYY");
  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");
  const tomorrowRange = useMemo(() => [tomorrow, tomorrow], [tomorrow]);

  const { data: tomorrowOrders } = useOrders(null, tomorrowRange);

  const todayStock = stockData[todayDateId] || {};
  // Bundles also claim their crate (packagingMaterial), like the stock API does.
  const productsByProductId = useMemo(() => new Map((products || []).map((p) => [p.productId, p])), [products]);
  const quantityInOrder = useCallback(
    (order, productId) => {
      const line = cartStockLines(order.userShoppingCart, productsByProductId).get(productId);
      return line ? line.direct + line.viaBundle : 0;
    },
    [productsByProductId],
  );

  // Calculate packedStock per product from packed orders with tomorrow's delivery date
  // Exclude administrativelyPacked orders since they weren't actually picked
  const packedStockByProduct = useMemo(() => {
    const result = {};
    (tomorrowOrders || [])
      .filter(
        (o) =>
          o.packed &&
          !o.administrativelyPacked &&
          o.farmorderStatus !== ORDER_STATUS_CANCELED &&
          o.farmorderStatus !== ORDER_STATUS_CANCELLED,
      )
      .forEach((order) => {
        for (const [productId, line] of cartStockLines(order.userShoppingCart, productsByProductId)) {
          result[productId] = (result[productId] || 0) + line.direct + line.viaBundle;
        }
      });
    return result;
  }, [tomorrowOrders, productsByProductId]);

  // Calculate requestedStock per product from all active orders (excluding administrativelyPacked)
  // This represents what actually needs to be picked
  const requestedStockByProduct = useMemo(() => {
    const result = {};
    (tomorrowOrders || [])
      .filter(
        (o) =>
          !o.administrativelyPacked &&
          o.farmorderStatus !== ORDER_STATUS_CANCELED &&
          o.farmorderStatus !== ORDER_STATUS_CANCELLED,
      )
      .forEach((order) => {
        for (const [productId, line] of cartStockLines(order.userShoppingCart, productsByProductId)) {
          result[productId] = (result[productId] || 0) + line.direct + line.viaBundle;
        }
      });
    return result;
  }, [tomorrowOrders, productsByProductId]);

  const tableData = useMemo(() => {
    let data = Object.entries(todayStock)
      .map(([productId, stock]) => {
        const product = (products || []).find((p) => p.productId === productId);
        const countedStock = stock.countedStock || 0;
        // Use calculated requestedStock that excludes administrativelyPacked orders
        const requestedStock = requestedStockByProduct[productId] || 0;
        const packedStock = packedStockByProduct[productId] || 0;
        const liveStock = Math.max(0, countedStock - packedStock);
        const toBePacked = Math.max(0, requestedStock - packedStock);
        const currentStock = liveStock - toBePacked;

        return {
          key: productId,
          productId,
          productName: product?.productName || productId,
          productCompany: product?.company,
          liveStock,
          packedStock,
          requestedStock,
          toBePacked,
          currentStock,
        };
      })
      .filter((row) => row.requestedStock > 0 || row.packedStock > 0 || row.liveStock > 0);

    // Apply company filter
    if (companyFilter && companyFilter !== "All") {
      data = data.filter((row) => row.productCompany === companyFilter);
    }

    // Apply search filter
    if (searchText) {
      data = data.filter(
        (row) =>
          row.productId.toLowerCase().includes(searchText.toLowerCase()) ||
          (row.productName && row.productName.toLowerCase().includes(searchText.toLowerCase())),
      );
    }

    return data.sort((a, b) => a.currentStock - b.currentStock);
  }, [todayStock, products, packedStockByProduct, requestedStockByProduct, companyFilter, searchText]);

  const openDrawer = (record) => {
    setSelectedProduct(record);
    setDrawerOpen(true);
  };

  const drawerOrders = useMemo(() => {
    if (!selectedProduct || !tomorrowOrders) return { packed: [], pending: [] };

    const activeOrders = tomorrowOrders.filter(
      (o) => o.farmorderStatus !== ORDER_STATUS_CANCELED && o.farmorderStatus !== ORDER_STATUS_CANCELLED,
    );

    const ordersWithProduct = activeOrders.filter((o) => quantityInOrder(o, selectedProduct.productId) > 0);

    return {
      packed: ordersWithProduct.filter((o) => o.packed && !o.administrativelyPacked),
      pending: ordersWithProduct.filter((o) => !o.packed),
    };
  }, [selectedProduct, tomorrowOrders, quantityInOrder]);

  const dropdownMenu = (record) => (
    <Menu>
      <Menu.Item onClick={() => openDrawer(record)}>
        <Flex alignItems="center">
          <EyeOutlined />
          <span style={{ marginLeft: 4 }}>{t("view")}</span>
        </Flex>
      </Menu.Item>
    </Menu>
  );

  const orderColumns = (statusLabel, badgeStatus) => [
    {
      title: t("order.order"),
      dataIndex: "farmorderId",
      render: (val) => <Text strong>#{val}</Text>,
    },
    {
      title: t("company"),
      dataIndex: "farmorderCustomerName",
    },
    {
      title: t("quantity"),
      render: (_, order) => quantityInOrder(order, selectedProduct?.productId),
    },
    {
      title: t("status"),
      render: () => <Badge status={badgeStatus} text={statusLabel} />,
    },
  ];

  const columns = [
    {
      title: t("product"),
      dataIndex: "productName",
      key: "productName",
      width: 240,
      fixed: "left",
      sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
      render: (text, record) => (
        <a
          onClick={(e) => {
            e.stopPropagation();
            openDrawer(record);
          }}
          style={{ color: "#880e47" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: t("live_stock.live_stock"),
      dataIndex: "liveStock",
      key: "liveStock",
      width: 150,
      sorter: (a, b) => utils.antdTableSorter(a, b, "liveStock"),
      render: (val) => <span style={{ color: val <= 0 ? "#f5222d" : val <= 5 ? "#faad14" : "#52c41a" }}>{val}</span>,
      onCell: () => ({
        style: {
          backgroundColor: "rgba(136, 14, 79, 0.05)",
        },
      }),
      onHeaderCell: () => ({
        style: {
          backgroundColor: "rgba(136, 14, 79, 0.05)",
        },
      }),
    },
    {
      title: t("live_stock.packed_stock"),
      dataIndex: "packedStock",
      key: "packedStock",
      width: 150,
      sorter: (a, b) => utils.antdTableSorter(a, b, "packedStock"),
      render: (val) => <span>{val}</span>,
    },
    {
      title: t("live_stock.to_be_packed"),
      dataIndex: "toBePacked",
      key: "toBePacked",
      width: 150,
      sorter: (a, b) => utils.antdTableSorter(a, b, "toBePacked"),
      render: (val) => <span style={{ color: val > 0 ? "#1890ff" : "inherit" }}>{val}</span>,
      onCell: () => ({
        style: {
          backgroundColor: "rgba(136, 14, 79, 0.05)",
        },
      }),
      onHeaderCell: () => ({
        style: {
          backgroundColor: "rgba(136, 14, 79, 0.05)",
        },
      }),
    },
    {
      title: t("live_stock.stock_delta"),
      dataIndex: "currentStock",
      key: "currentStock",
      width: 150,
      sorter: (a, b) => utils.antdTableSorter(a, b, "currentStock"),
      defaultSortOrder: "ascend",
      render: (val) => <span style={{ color: val < 0 ? "#f5222d" : val === 0 ? "#faad14" : "#52c41a" }}>{val}</span>,
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(record)} />
        </div>
      ),
    },
  ];

  const handleCompanyFilterChange = (value) => {
    localStorage.setItem("liveStockCompanyFilter", value);
    setCompanyFilter(value);
  };

  return (
    <>
      {!isMobileView ? (
        <Space style={{ marginBottom: 16 }} size="small">
          <StyledSelect value={companyFilter} onChange={handleCompanyFilterChange} style={{ width: 160 }}>
            <Option value="All">{t("all_companies")}</Option>
            {PRODUCT_COMPANY_GROUPS.map((group) => (
              <Option key={group.value} value={group.value}>
                {group.label}
              </Option>
            ))}
          </StyledSelect>
          <Input
            placeholder={t("search_product")}
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            style={{ width: 250 }}
          />
        </Space>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <Popover
            content={
              <FilterPopoverContent>
                <div>
                  <FilterLabel>{t("search")}</FilterLabel>
                  <Input
                    value={searchText}
                    placeholder={t("search_product")}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => setSearchText(e.target.value)}
                    size="large"
                  />
                </div>
                <div>
                  <FilterLabel>{t("company")}</FilterLabel>
                  <Select
                    placeholder={t("company")}
                    style={{ width: "100%" }}
                    value={companyFilter}
                    onChange={handleCompanyFilterChange}
                    size="large"
                  >
                    <Option value="All">{t("all_companies")}</Option>
                    {PRODUCT_COMPANY_GROUPS.map((group) => (
                      <Option key={group.value} value={group.value}>
                        {group.label}
                      </Option>
                    ))}
                  </Select>
                </div>
                <FilterButtonsContainer>
                  <Button
                    onClick={() => {
                      localStorage.removeItem("liveStockCompanyFilter");
                      setSearchText("");
                      setCompanyFilter("All");
                      setFilterPopoverVisible(false);
                    }}
                    style={{ flex: 1 }}
                  >
                    {t("reset")}
                  </Button>
                  <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                    {t("apply")}
                  </Button>
                </FilterButtonsContainer>
              </FilterPopoverContent>
            }
            title={t("filters")}
            trigger="click"
            open={filterPopoverVisible}
            onOpenChange={setFilterPopoverVisible}
            placement="bottomLeft"
          >
            <Button
              icon={<FilterOutlined />}
              style={{
                borderColor: companyFilter !== "All" || searchText ? "#FF4B33" : "",
                color: companyFilter !== "All" || searchText ? "#FF4B33" : "",
              }}
            />
          </Popover>
        </div>
      )}
      <div className="table-responsive">
        <StyledLiveStockTable
          columns={columns}
          dataSource={tableData}
          rowKey="productId"
          scroll={{ y: tableY }}
          pagination={false}
          showSorterTooltip={false}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("live_stock.no_data")} />,
          }}
        />
      </div>

      <Drawer title={selectedProduct?.productName} open={drawerOpen} onClose={() => setDrawerOpen(false)} width={800}>
        {selectedProduct && (
          <>
            <div style={{ marginBottom: 24 }}>
              <Text strong style={{ fontSize: 14 }}>
                {t("live_stock.packed_orders")}
              </Text>
              {drawerOrders.packed.length === 0 ? (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{t("live_stock.no_packed_orders")}</Text>
                </div>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  style={{ marginTop: 8 }}
                  dataSource={drawerOrders.packed}
                  columns={orderColumns(t("live_stock.packed"), "success")}
                />
              )}
            </div>

            <div>
              <Text strong style={{ fontSize: 14 }}>
                {t("live_stock.pending_orders")}
              </Text>
              {drawerOrders.pending.length === 0 ? (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{t("live_stock.no_pending_orders")}</Text>
                </div>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  style={{ marginTop: 8 }}
                  dataSource={drawerOrders.pending}
                  columns={orderColumns(t("live_stock.to_be_packed"), "processing")}
                />
              )}
            </div>
          </>
        )}
      </Drawer>
    </>
  );
};

export default LiveStockTab;

const StyledLiveStockTable = styled(Table)`
  div.ant-table-body {
    max-height: 60vh !important;
  }

  /* Alternating row colors */
  .ant-table-tbody > tr:nth-child(odd) {
    background-color: #ffffff;
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: #fafafa;
  }

  /* Hover effects that work with both patterns */
  .ant-table-tbody > tr:hover > td {
    background-color: #f3e7ed !important;
  }
`;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;
